import React, { useState } from 'react'
import cn, { Argument as ClassName } from 'classnames'
import { faCaretUp, faCaretDown } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import styled from 'styled-components'
import { Link } from 'react-router-dom'

import { Button } from '../button'
import Text from '../../ui/Text'
import { Company } from '../loadCompanies'
import { IPartner } from '../loadPartners'
import { WidgetLayout } from '../widget-layout'
import { sortByString } from '../sort-by-string'
import { SInput } from '../../support/features/charge-point-capi-details-widget/charge-point-capi-details-widget.style'
import { MoveOrganisationsModal } from '../move-organisations-modal'
import BootstrapModalLayout from '../../ui/BootstrapModalLayout'
import { filterFieldsBySearchValue } from '../filter-fields-by-search-value'
import { Table, TableBody, TableBodyCell, TableHeader, TableHeaderCell, TableRow } from '../table'
import { STableLink } from '../table/Table.styles'
import useText from '../../common/hooks/useText'

const SSearchForm = styled.div`
    padding-bottom: 20px;
    width: 100%;

    @media (min-width: 900px) {
        width: 30%;
    }
`

const SOrganisation = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
`

const SIcon = styled(FontAwesomeIcon)`
    margin-left: 4px;
`

const SCustomCheckboxContainer = styled.div`
    width: 16px;
    display: inline-block;
`

const SCustomCheckbox = styled.input<{ dashed?: boolean }>`
    appearance: none;
    position: relative;

    &:checked:after {
        background-color: #0075ff;
        border-color: #0033a0;
    }

    &:checked:before {
        border-color: #ffffff;
    }

    &:after {
        content: '';
        position: absolute;
        bottom: -2px;
        left: 0;
        width: 14px;
        height: 14px;
        border: 1px solid #cccccc;
        border-radius: 4px;
        background-color: #f8f8f8;
    }

    &:before {
        content: '';
        position: absolute;
        width: 8px;
        height: 5px;
        border-bottom: 2px solid #f8f8f8;
        z-index: 1;
        left: 3px;
        bottom: 4px;
        transform: rotate(312deg);
        border-left: 2px solid #f8f8f8;
    }

    ${({ dashed }) => {
        if (!dashed) return
        return `&:before {
            transform: none;
            border-left: none;
        }`
    }}
`

export type PartnerOrganisationsWidgetProps = {
    partner: IPartner
    reload: () => void
    organisations?: Array<Company>
    className?: ClassName
    style?: React.CSSProperties
}

enum SortParameter {
    NAME = 'name',
    DESCRIPTION = 'description',
    EMAIL = 'email',
    PHONE = 'phone',
}

export const PartnerOrganisationsWidget = React.memo(function PartnerOrganisationsWidget({
    partner,
    reload,
    organisations,
    className,
    style,
}: PartnerOrganisationsWidgetProps): React.ReactElement {
    const text = useText()
    const [search, setSearch] = useState('')
    const [selectedOrganisations, setSelectedOrganisations] = useState<Array<Company>>([])
    const [isMoveOrganisationsModalOpened, setIsMoveOrganisationsModalOpened] = useState(false)
    const [sortFilter, setSortFilter] = useState<{ parameter: SortParameter; reversed?: boolean }>({
        parameter: SortParameter.NAME,
    })

    const selectAllOrganisations = () => {
        if (!organisations || selectedOrganisations.length === organisations.length) {
            setSelectedOrganisations([])
            return
        }
        setSelectedOrganisations(
            organisations.filter((organisation) =>
                filterFieldsBySearchValue(
                    [organisation.name, organisation.description, organisation.email || '', organisation.phone],
                    search
                )
            )
        )
    }

    const selectOrganisation = (organisation: Company) => {
        if (selectedOrganisations.includes(organisation)) {
            setSelectedOrganisations(selectedOrganisations.filter((org) => org.id !== organisation.id))
            return
        }
        setSelectedOrganisations([...selectedOrganisations, organisation])
    }

    return (
        <WidgetLayout
            className={cn(className)}
            style={style}
            title={text('lblOrganisations')}
            headerButtons={
                <Button
                    small
                    onClick={() => setIsMoveOrganisationsModalOpened(true)}
                    disabled={!selectedOrganisations.length}
                    title={text('btnMove')}
                    dataTestId="moveOrganisationBtn"
                />
            }
            data-testid="partnerOrganisation"
        >
            {isMoveOrganisationsModalOpened && (
                <BootstrapModalLayout>
                    <MoveOrganisationsModal
                        close={() => setIsMoveOrganisationsModalOpened(false)}
                        organisations={selectedOrganisations}
                        currentPartner={partner}
                        onMove={() => {
                            setSelectedOrganisations([])
                            reload()
                        }}
                    />
                </BootstrapModalLayout>
            )}
            <SSearchForm>
                <SInput
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder={text('lblSearchField')}
                />
            </SSearchForm>
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHeaderCell>
                            <SOrganisation>
                                <SCustomCheckboxContainer>
                                    <SCustomCheckbox
                                        type="checkbox"
                                        onChange={selectAllOrganisations}
                                        dashed={
                                            !!selectedOrganisations.length &&
                                            !!organisations &&
                                            selectedOrganisations.length < organisations.length
                                        }
                                        checked={!!selectedOrganisations.length}
                                    />
                                </SCustomCheckboxContainer>
                                <STableLink
                                    onClick={() =>
                                        setSortFilter({
                                            parameter: SortParameter.NAME,
                                            reversed: !sortFilter?.reversed,
                                        })
                                    }
                                    data-testid="nameLabel"
                                >
                                    <Text code="lblName" />
                                    {sortFilter?.parameter === SortParameter.NAME && (
                                        <SIcon icon={sortFilter.reversed ? faCaretUp : faCaretDown} />
                                    )}
                                </STableLink>
                            </SOrganisation>
                        </TableHeaderCell>
                        <TableHeaderCell>
                            <STableLink
                                onClick={() =>
                                    setSortFilter({
                                        parameter: SortParameter.DESCRIPTION,
                                        reversed: !sortFilter?.reversed,
                                    })
                                }
                                data-testid="descLabel"
                            >
                                <Text code="lblDescription" />
                                {sortFilter?.parameter === SortParameter.DESCRIPTION && (
                                    <SIcon icon={sortFilter.reversed ? faCaretUp : faCaretDown} />
                                )}
                            </STableLink>
                        </TableHeaderCell>
                        <TableHeaderCell>
                            <STableLink
                                onClick={() =>
                                    setSortFilter({
                                        parameter: SortParameter.EMAIL,
                                        reversed: !sortFilter?.reversed,
                                    })
                                }
                                data-testid="emailLabel"
                            >
                                <Text code="lblEmail" />
                                {sortFilter?.parameter === SortParameter.EMAIL && (
                                    <SIcon icon={sortFilter.reversed ? faCaretUp : faCaretDown} />
                                )}
                            </STableLink>
                        </TableHeaderCell>
                        <TableHeaderCell>
                            <STableLink
                                onClick={() =>
                                    setSortFilter({
                                        parameter: SortParameter.PHONE,
                                        reversed: !sortFilter?.reversed,
                                    })
                                }
                                data-testid="phoneLabel"
                            >
                                <Text code="lblPhone" />
                                {sortFilter?.parameter === SortParameter.PHONE && (
                                    <SIcon icon={sortFilter.reversed ? faCaretUp : faCaretDown} />
                                )}
                            </STableLink>
                        </TableHeaderCell>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {organisations
                        ?.filter((organisation) =>
                            filterFieldsBySearchValue(
                                [
                                    organisation.name,
                                    organisation.description,
                                    organisation.email || '',
                                    organisation.phone,
                                ],
                                search
                            )
                        )
                        .sort((a, b) =>
                            sortByString(
                                (a[sortFilter.parameter] as string) ?? '',
                                (b[sortFilter.parameter] as string) ?? '',
                                sortFilter.reversed
                            )
                        )
                        .map((organisation, i) => (
                            <TableRow key={Math.random()}>
                                <TableBodyCell>
                                    <SOrganisation>
                                        <SCustomCheckboxContainer>
                                            <SCustomCheckbox
                                                type="checkbox"
                                                onChange={() => selectOrganisation(organisation)}
                                                checked={selectedOrganisations.includes(organisation)}
                                            />
                                        </SCustomCheckboxContainer>
                                        <STableLink
                                            as={Link}
                                            to={`/company/${organisation.id}`}
                                            data-testid={'org-' + i}
                                        >
                                            {organisation.name}
                                        </STableLink>
                                    </SOrganisation>
                                </TableBodyCell>
                                <TableBodyCell data-testid={'orgDesc-' + i}>{organisation.description}</TableBodyCell>
                                <TableBodyCell data-testid={'orgEmail-' + i}>{organisation.email}</TableBodyCell>
                                <TableBodyCell data-testid={'orgPhone-' + i}>{organisation.phone}</TableBodyCell>
                            </TableRow>
                        ))}
                </TableBody>
            </Table>
        </WidgetLayout>
    )
})
